import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Trash2, Shirt, Upload, Tag } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api, errMsg, fileUrl, uploadImage, CATEGORIES } from "@/lib/api";
import { Page, Empty } from "@/components/Page";

const inp = "h-11 w-full rounded-xl border border-white/10 bg-[#0A0A0B] px-4 text-sm outline-none focus:border-[#D4AF37]/60";
const EMPTY = { name: "", category: "Oberteil", brand: "", color: "#1C1C1E", color_name: "", season: "ganzjährig", image_path: null };

export default function Wardrobe() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");
  const navigate = useNavigate();

  const load = () => api.get("/wardrobe").then(({ data }) => setItems(data));
  useEffect(() => { load(); }, []);

  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try { setForm({ ...form, image_path: await uploadImage(f) }); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.image_path) return toast.error("Bitte ein Foto hinzufügen");
    try {
      await api.post("/wardrobe", form);
      toast.success("Zum Kleiderschrank hinzugefügt");
      setOpen(false); setForm(EMPTY); load();
    } catch (err) { toast.error(errMsg(err)); }
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const shown = items.filter((i) => !filter || i.category === filter);

  return (
    <Page over="Mein Kleiderschrank" title="Was du zuhause hast" sub="Digitalisiere deine Klamotten. vyne bezieht sie in Outfit-Vorschläge ein – und du kannst sie mit einem Klick verkaufen."
      right={<button onClick={() => setOpen(true)} className="btn-cream" data-testid="wardrobe-add-button"><Plus className="h-4 w-4" /> Teil hinzufügen</button>} testid="wardrobe-page">
      <div className="mb-8 flex flex-wrap gap-2">
        {["", ...CATEGORIES].map((c) => (
          <button key={c || "all"} onClick={() => setFilter(c)} className={`rounded-full border px-4 py-1.5 text-xs transition-colors ${filter === c ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/10 text-neutral-400"}`} data-testid={`wardrobe-filter-${c || "alle"}`}>{c || "Alle"}</button>
        ))}
      </div>
      {shown.length === 0 ? (
        <Empty icon={Shirt} text="Noch keine Teile. Fotografiere dein Lieblingsshirt, deine Jeans oder Sneaker und lege los." testid="wardrobe-empty">
          <button onClick={() => setOpen(true)} className="btn-ghost">Erstes Teil hinzufügen</button>
        </Empty>
      ) : (
        <div className="grid grid-cols-2 gap-5 md:grid-cols-3 lg:grid-cols-4" data-testid="wardrobe-grid">
          {shown.map((w) => (
            <div key={w.id} className="card-lux group overflow-hidden" data-testid={`wardrobe-item-${w.id}`}>
              <div className="aspect-square overflow-hidden bg-[#121215]"><img src={fileUrl(w.image_path)} alt={w.name} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" /></div>
              <div className="p-4">
                <div className="flex items-center gap-2"><span className="h-3 w-3 rounded-full border border-white/20" style={{ background: w.color }} /><p className="text-[10px] uppercase tracking-widest text-neutral-500">{w.category}</p></div>
                <p className="mt-1 truncate font-serif text-lg">{w.name}</p>
                <p className="text-xs text-neutral-500">{w.brand || "–"}</p>
                <div className="mt-3 flex gap-2">
                  <button onClick={() => navigate(`/marktplatz/verkaufen?from=${w.id}`)} className="btn-ghost flex-1 !px-3 !py-2 text-xs" data-testid={`wardrobe-sell-${w.id}`}><Tag className="h-3.5 w-3.5" /> Verkaufen</button>
                  <button onClick={async () => { await api.delete(`/wardrobe/${w.id}`); load(); }} className="btn-ghost !px-3 !py-2" data-testid={`wardrobe-delete-${w.id}`}><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg border-white/10 bg-[#121215]" data-testid="wardrobe-dialog">
          <DialogHeader><DialogTitle className="font-serif text-2xl">Neues Teil</DialogTitle></DialogHeader>
          <form onSubmit={submit} className="space-y-4">
            <label className="flex h-40 cursor-pointer items-center justify-center overflow-hidden rounded-xl border border-dashed border-white/15 bg-[#0A0A0B] hover:border-[#D4AF37]/50" data-testid="wardrobe-photo-label">
              {form.image_path ? <img src={fileUrl(form.image_path)} alt="" className="h-full w-full object-cover" /> : <span className="flex items-center gap-2 text-sm text-neutral-400"><Upload className="h-4 w-4" />{busy ? "Lädt…" : "Foto hochladen"}</span>}
              <input type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onFile} data-testid="wardrobe-photo-input" />
            </label>
            <input required value={form.name} onChange={set("name")} placeholder="Bezeichnung, z.B. Weißes Oxford-Hemd" className={inp} data-testid="wardrobe-name-input" />
            <div className="grid grid-cols-2 gap-3">
              <select value={form.category} onChange={set("category")} className={inp} data-testid="wardrobe-category-select">{CATEGORIES.map((c) => <option key={c}>{c}</option>)}</select>
              <input value={form.brand} onChange={set("brand")} placeholder="Marke" className={inp} data-testid="wardrobe-brand-input" />
            </div>
            <div className="grid grid-cols-[auto,1fr,1fr] items-center gap-3">
              <input type="color" value={form.color} onChange={set("color")} className="h-11 w-14 cursor-pointer rounded-xl border border-white/10 bg-transparent" data-testid="wardrobe-color-input" />
              <input value={form.color_name} onChange={set("color_name")} placeholder="Farbe, z.B. Navy" className={inp} data-testid="wardrobe-colorname-input" />
              <select value={form.season} onChange={set("season")} className={inp} data-testid="wardrobe-season-select">{["ganzjährig", "Frühling", "Sommer", "Herbst", "Winter"].map((s) => <option key={s}>{s}</option>)}</select>
            </div>
            <button type="submit" disabled={busy} className="btn-cream w-full" data-testid="wardrobe-submit-button">Speichern</button>
          </form>
        </DialogContent>
      </Dialog>
    </Page>
  );
}
